<template>
  <div
    class="font-primary font-medium text-[17px] leading-[18px] text-gray-800 pb-6 border-b border-[#DFDFDF]"
  >
    Preferences
  </div>

  <SettingsRow>
    <template #left><div class="my-auto">Default file privacy</div></template>

    <template #right>
      <div v-if="user" class="flex">
        <div class="mr-4">{{ capitalize(user.defaultFilePrivacy) }}</div>
        <div
          class="font-medium text-[14px] text-green-700 cursor-pointer"
          @click="emit('openDialog', 'DefaultFilePrivacy')"
        >
          Edit
        </div>
      </div>
      <Skeleton v-else class="w-[60px] h-[18px]" />
    </template>
  </SettingsRow>

  <SettingsRow>
    <template #left><div class="my-auto">Date format</div></template>

    <template #right>
      <UiSelect
        v-if="user"
        :modelValue="dateFormat"
        @update:modelValue="emit('updateDateFormat', $event)"
        label="Date format"
        :options="DATE_FORMATS"
        class="max-w-[200px]"
        :flat="isMobile"
      />
      <Skeleton v-else class="w-[198px] h-[46px]" />
    </template>
  </SettingsRow>

  <div class="mb-11 border-b border-[#DFDFDF] py-6">
    <SettingsRow class="border-none h-auto">
      <template #left><div class="my-auto">Automatic timezone</div></template>

      <template #right>
        <div v-if="user" class="flex items-center">
          <div v-if="automaticTimezone" class="mr-4">
            {{ currentTimezoneLabel }}
          </div>
          <Toggle
            :modelValue="automaticTimezone"
            @update:modelValue="emit('updateAutomaticTimezone', $event)"
          />
        </div>
        <Skeleton v-else class="w-[30px] h-[18px]" />
      </template>
    </SettingsRow>
    <div v-if="!automaticTimezone">
      <div class="mt-4 flex items-center justify-end">
        <UiSelect
          :modelValue="customTimezone.country"
          @update:modelValue="emit('updateCustomTimezoneCountry', $event)"
          label="Country"
          :options="countryOptions"
          class="w-1/2 mobile-l:min-w-[170px] mobile-l:w-fit mr-3"
          :flat="isMobile"
        />

        <UiSelect
          :modelValue="customTimezone.name"
          @update:modelValue="emit('updateCustomTimezoneName', $event)"
          label="Timezone"
          :options="timezoneOptions"
          class="w-1/2 mobile-l:min-w-[170px] mobile-l:w-fit"
          :flat="isMobile"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SettingsRow from '@/features/settings/components/SettingsRow.vue'
import UiSelect from '@/shared/components/UiSelect.vue'
import { Toggle, Skeleton } from '@dacr/ui'
import { capitalize } from '@/shared/utils/string'
import type {
  UserFieldsFragment,
  TimezoneInput,
} from '@/shared/graphql/legacy/generated'

const DATE_FORMATS = [
  { value: 'd/M/yyyy', label: 'DD/MM/YYYY' },
  { value: 'M/d/yyyy', label: 'MM/DD/YYYY' },
  { value: 'yyyy/M/d', label: 'YYYY/MM/DD' },
]

defineProps<{
  user: UserFieldsFragment | null
  dateFormat: string
  automaticTimezone: boolean
  currentTimezoneLabel?: string
  customTimezone: TimezoneInput
  countryOptions: { label: string; value: string }[]
  timezoneOptions: { label: string; value: string }[]
  isMobile: boolean
}>()

const emit = defineEmits<{
  (e: 'openDialog', name: string): void
  (e: 'updateDateFormat', value: string): void
  (e: 'updateAutomaticTimezone', value: boolean): void
  (e: 'updateCustomTimezoneCountry', value: string): void
  (e: 'updateCustomTimezoneName', value: string): void
}>()
</script>
